import type { ReactNode } from 'react';

/**
 * The page geometry the portal routes share with their `pendingComponent` skeletons (page-pending.tsx).
 * A skeleton only reads as the page it stands in for while the two agree on their wrappers, and a
 * skeleton that copied the class strings quietly stops matching the first time the page moves. Both
 * sides render these instead, so the padding, max widths and backgrounds can only change together.
 *
 * Only the shells with a skeleton counterpart live here. Everything inside them (the campaign
 * metadata row, the sticky donate button, the testimonial and FAQ sections) stays in its route.
 */

const PAGE_MAX_WIDTH = 'mx-auto w-full max-w-[1366px]';

/** Row holding the "back to" link at the top of a campaign page, and its skeleton. */
export const BACK_LINK_ROW_CLASS = 'mb-1 px-5 py-4 sm:mb-0 sm:p-6';

/** Legal content column, shared by the rendered article and its skeleton. */
export const LEGAL_CONTENT_CLASS = 'mx-auto max-w-4xl p-8';

export function HomeLayout(props: { children: ReactNode }) {
	return <div className="flex min-h-svh w-full">{props.children}</div>;
}

export function HomeMain(props: { children: ReactNode }) {
	return <main className="flex min-w-0 flex-1 flex-col">{props.children}</main>;
}

export function HeroSection(props: { children: ReactNode }) {
	return <section className={PAGE_MAX_WIDTH}>{props.children}</section>;
}

export function CampaignsSection(props: { children: ReactNode }) {
	return (
		<div id="campaigns" className="bg-secondary border-border flex-1 border-t">
			<div className="mx-auto flex max-w-[1366px] flex-col gap-6 px-5 py-8 sm:px-6 sm:py-16 lg:gap-12 lg:py-24">
				{props.children}
			</div>
		</div>
	);
}

/* We're wrapping everything with the secondary color because we need the sticky donate button at the
   same DOM level so it can work properly, and also we want to avoid white-on-grey color when showing
   the donate button at bottom in non-sticky mode, hence the secondary color. */
export function CampaignLayout(props: { children: ReactNode }) {
	return <div className="bg-secondary">{props.children}</div>;
}

/** Everything above the fold on a campaign page: back link, hero and gallery. */
export function CampaignTopSection(props: { children: ReactNode }) {
	return <div className="bg-background pb-2 sm:pb-6">{props.children}</div>;
}

export function CampaignBackNav(props: { children: ReactNode }) {
	return <nav className={PAGE_MAX_WIDTH}>{props.children}</nav>;
}

/** Everything below the fold on a campaign page: metadata row and the two content columns. */
export function CampaignBottomSection(props: { children: ReactNode }) {
	return <div className="bg-secondary border-border border-t">{props.children}</div>;
}

/** Campaign description column + details aside. */
export function CampaignColumns(props: { children: ReactNode }) {
	return (
		<main className="mx-auto flex w-full max-w-[1366px] flex-col-reverse gap-6 px-5 pt-5 pb-6 sm:gap-12 sm:px-6 sm:pt-6 lg:flex-row lg:gap-24 lg:pb-16">
			{props.children}
		</main>
	);
}

export function LegalLayout(props: { children: ReactNode }) {
	return <main className="w-full">{props.children}</main>;
}
